import {Platform} from 'react-native';
import i18n from 'i18next';
import apiClient, {uploadFile} from './api-client';
import {APP_VERSION} from '../config/appHeaders';

// Feedback API (Bruno: "FeedbackCustomer (app / web)"):
//   POST upload-file          multipart "file" -> Data.url  (photo first)
//   POST feedback             JSON {subject, message, source, appVersion,
//                             deviceInfo, attachmentUrl?, placeId?}
//                             subject max 150, message max 500.
//                             400: Data.errors [{field, message}]
//                             429: TOO_MANY_FEEDBACK (10 per day)
//   GET  feedback?page&limit  the user's own tickets, newest first
//                             -> Data.feedback[], Data.pagination
//   GET  feedback/:id         one ticket with the replies that were emailed
// The x-lang header (en | du) also sets the language of the reply email.

export const SUBJECT_MAX = 150;
export const MESSAGE_MAX = 500;

const langHeader = () => ({'x-lang': i18n.language === 'dt' ? 'du' : 'en'});

// e.g. "ios 18.1", "android 34", or the browser's user agent on the web.
const deviceInfo = () => {
  if (Platform.OS === 'web') {
    return typeof navigator !== 'undefined'
      ? String(navigator.userAgent).slice(0, 200)
      : 'web';
  }
  return `${Platform.OS} ${Platform.Version}`;
};

const body = error => error?.response?.data;

const errorMessage = (error, fallback) =>
  body(error)?.Message || body(error)?.message || error?.message || fallback;

// Data.errors [{field, message}] -> {subject: '...', message: '...'}
const fieldErrors = error => {
  const list = body(error)?.Data?.errors;
  if (!Array.isArray(list)) {
    return undefined;
  }
  return list.reduce((acc, item) => {
    if (item?.field) {
      acc[item.field] = item.message;
    }
    return acc;
  }, {});
};

/**
 * Send the user's feedback, with an optional photo.
 * @param {{subject: string, message: string, placeId?: string,
 *   file?: {uri: string, type: string, name: string}}} feedback
 * @returns {Promise<{success: boolean, data?: object, error?: string,
 *   fieldErrors?: object, tooMany?: boolean}>}
 */
export const sendFeedback = async ({subject, message, placeId, file}) => {
  let attachmentUrl;
  if (file?.uri) {
    const upload = await uploadFile(file);
    attachmentUrl = upload.data?.Data?.url ?? upload.data?.data?.url;
    if (!upload.success || !attachmentUrl) {
      return {
        success: false,
        error:
          upload.error?.Message ||
          upload.error?.message ||
          (typeof upload.error === 'string' ? upload.error : null) ||
          'The photo could not be uploaded',
      };
    }
  }

  try {
    const response = await apiClient.post(
      'feedback',
      {
        subject,
        message,
        source: Platform.OS, // ios | android | web
        appVersion: APP_VERSION,
        deviceInfo: deviceInfo(),
        ...(attachmentUrl ? {attachmentUrl} : {}),
        ...(placeId ? {placeId} : {}),
      },
      {headers: langHeader(), timeout: 30000},
    );
    const json = response.data;
    if (json?.Success === 0 || json?.success === false) {
      return {success: false, error: json?.Message || json?.message};
    }
    return {success: true, data: json?.Data ?? json?.data};
  } catch (error) {
    return {
      success: false,
      error: errorMessage(error, 'Sending failed'),
      fieldErrors: fieldErrors(error),
      tooMany: error?.response?.status === 429,
    };
  }
};

/**
 * The logged-in user's own feedback tickets, newest first.
 * @returns {Promise<{success: boolean, feedback?: object[], pagination?: object, error?: string}>}
 */
export const getMyFeedback = async ({page = 1, limit = 20} = {}) => {
  try {
    const response = await apiClient.get('feedback', {
      params: {page, limit},
      headers: langHeader(),
    });
    const data = response.data?.Data ?? response.data?.data;
    return {
      success: true,
      feedback: Array.isArray(data?.feedback) ? data.feedback : [],
      pagination: data?.pagination,
    };
  } catch (error) {
    return {success: false, error: errorMessage(error, 'Could not load')};
  }
};

/**
 * One of the user's tickets, with the replies that were emailed.
 * @param {number|string} id
 */
export const getFeedbackById = async id => {
  try {
    const response = await apiClient.get(`feedback/${id}`, {
      headers: langHeader(),
    });
    return {success: true, data: response.data?.Data ?? response.data?.data};
  } catch (error) {
    return {
      success: false,
      status: error?.response?.status,
      error: errorMessage(error, 'Could not load'),
    };
  }
};
